/* ============================================================
   supplement-facts-csv.js

   <supplement-facts-csv> wraps the PDP v4 supplement facts table
   and fills it from CSV file metafields, client-side.

   Attributes / children:
   - data-src          Initial CSV URL (selected variant's file, or product file)
   - data-product-src  Product-level CSV URL (fallback for variants without one)
   - <script type="application/json" data-variant-csv-map>
                       { "variantId": "csv url", ... } for variants with
                       their own file.

   Resolution order per variant:
     variant CSV -> product CSV -> static server-rendered rows.

   Variant switching: main-product-v4.js writes the selected variant id
   to the data-variant attribute on <main-product-v4>; this component
   observes that attribute and swaps the table. Parsed files are cached
   per URL, so switching back and forth never re-fetches.

   Expected CSV columns (first row = header, detected and skipped):
     Label, Amount per serving, % Daily Value, Indent
   - Indent: "yes" / "true" / "1" indents the row (sub-row style).
   - On any error the previous/static rows are left untouched.
   ============================================================ */

if (!customElements.get("supplement-facts-csv")) {
  customElements.define(
    "supplement-facts-csv",
    class SupplementFactsCsv extends HTMLElement {
      connectedCallback() {
        this.csvCache = this.csvCache || {};
        this.currentSrc = null;
        this.staticRowsSnapshot = null;

        this.variantMap = {};
        const mapScript = this.querySelector("[data-variant-csv-map]");
        if (mapScript) {
          try {
            this.variantMap = JSON.parse(mapScript.textContent) || {};
          } catch (error) {
            console.warn("[supplement-facts-csv] Invalid variant CSV map:", error);
          }
        }

        // Watch the selected variant on the PDP root element
        this.mainProduct = this.closest("main-product-v4");
        if (this.mainProduct) {
          this.variantObserver = new MutationObserver(() => this.update());
          this.variantObserver.observe(this.mainProduct, {
            attributes: true,
            attributeFilter: ["data-variant"],
          });
        }

        this.update();
      }

      disconnectedCallback() {
        if (this.variantObserver) {
          this.variantObserver.disconnect();
          this.variantObserver = null;
        }
      }

      /* Pick the CSV for the current variant and swap the table.
         No URL at all -> restore the static server-rendered rows. */
      update() {
        const variantId = this.mainProduct
          ? this.mainProduct.getAttribute("data-variant")
          : null;

        const src =
          (variantId && this.variantMap[variantId]) ||
          this.dataset.productSrc ||
          this.dataset.src ||
          "";

        if (src === this.currentSrc) return;

        if (src === "") {
          this.restoreStaticRows();
          this.currentSrc = "";
          return;
        }

        this.load(src);
      }

      async load(src) {
        try {
          if (!this.csvCache[src]) {
            this.csvCache[src] = fetch(src).then((response) => {
              if (!response.ok) throw new Error(`HTTP ${response.status}`);
              return response.text();
            });
          }
          const rows = this.parseCsv(await this.csvCache[src]);
          if (rows.length === 0) throw new Error("CSV file is empty");
          this.render(rows);
          this.currentSrc = src;
        } catch (error) {
          // Failed fetches shouldn't be cached, so a later variant
          // switch back to this URL can retry.
          delete this.csvCache[src];
          console.warn("[supplement-facts-csv] Keeping current rows:", error);
        }
      }

      /* Minimal RFC 4180 parser: handles quoted fields, escaped quotes ("")
         and commas/newlines inside quotes. Returns an array of rows,
         each row an array of cell strings. Skips fully empty lines. */
      parseCsv(text) {
        const rows = [];
        let row = [];
        let cell = "";
        let inQuotes = false;

        for (let i = 0; i < text.length; i++) {
          const char = text[i];

          if (inQuotes) {
            if (char === '"') {
              if (text[i + 1] === '"') {
                cell += '"';
                i++;
              } else {
                inQuotes = false;
              }
            } else {
              cell += char;
            }
          } else if (char === '"') {
            inQuotes = true;
          } else if (char === ",") {
            row.push(cell);
            cell = "";
          } else if (char === "\n" || char === "\r") {
            if (char === "\r" && text[i + 1] === "\n") i++;
            row.push(cell);
            cell = "";
            if (row.some((value) => value.trim() !== "")) rows.push(row);
            row = [];
          } else {
            cell += char;
          }
        }

        // Flush the last row (file may not end with a newline)
        row.push(cell);
        if (row.some((value) => value.trim() !== "")) rows.push(row);

        return rows;
      }

      getTable() {
        return this.querySelector(".main-product-v4__facts-table");
      }

      getDataRows(table) {
        return table.querySelectorAll(
          ".main-product-v4__facts-row:not(.main-product-v4__facts-row--head)"
        );
      }

      /* Before the first CSV render, snapshot the server-rendered rows so
         we can restore them for variants with no CSV at all. */
      snapshotStaticRows(table) {
        if (this.staticRowsSnapshot !== null) return;
        this.staticRowsSnapshot = Array.from(this.getDataRows(table)).map((el) =>
          el.cloneNode(true)
        );
      }

      restoreStaticRows() {
        const table = this.getTable();
        if (!table || this.staticRowsSnapshot === null) return;
        this.getDataRows(table).forEach((el) => el.remove());
        this.staticRowsSnapshot.forEach((el) => table.append(el.cloneNode(true)));
        this.remeasureAccordion();
      }

      render(rows) {
        const table = this.getTable();
        if (!table) return;

        // Skip the first row when it looks like a header
        const firstRowText = rows[0].join(" ").toLowerCase();
        const dataRows =
          firstRowText.includes("label") || firstRowText.includes("amount")
            ? rows.slice(1)
            : rows;
        if (dataRows.length === 0) return;

        this.snapshotStaticRows(table);
        this.getDataRows(table).forEach((el) => el.remove());

        const fragment = document.createDocumentFragment();

        dataRows.forEach((cells) => {
          const [label, amount, dailyValue, indent] = cells.map((value) =>
            (value || "").trim()
          );
          if (label === "") return;

          const rowEl = document.createElement("div");
          rowEl.className = "main-product-v4__facts-row";
          if (/^(yes|true|1)$/i.test(indent || "")) {
            rowEl.classList.add("main-product-v4__facts-row--indent");
          }

          const labelEl = document.createElement("span");
          labelEl.className = "main-product-v4__facts-label";
          labelEl.textContent = label;

          const amountEl = document.createElement("span");
          amountEl.textContent = amount || "";

          const dailyValueEl = document.createElement("span");
          dailyValueEl.textContent = dailyValue || "";

          rowEl.append(labelEl, amountEl, dailyValueEl);
          fragment.append(rowEl);
        });

        table.append(fragment);
        this.remeasureAccordion();
      }

      /* If the accordion is open, its max-height was measured with the old
         rows — re-measure so nothing gets clipped after a swap. */
      remeasureAccordion() {
        const accordionContent = this.closest(".accordion__content");
        if (accordionContent && accordionContent.style.maxHeight) {
          accordionContent.style.maxHeight = accordionContent.scrollHeight + "px";
        }
      }
    }
  );
}
